import Window from "../contexts/Window";

/**
 * The one place a store's country becomes a flag code.
 *
 * A store's country reaches the frontend in several shapes, and usually NOT as a
 * 2-letter code: the store record carries a `country` relation or a `country_id`,
 * Store Settings records only the country NAME (`jurisdiction`), and
 * `/api/stores/accessible` sends no country at all. Every surface that shows a
 * store — sidebar switcher, bottom tab bar, anything added later — resolves it
 * through here, so they cannot drift apart and so the day the API starts sending
 * a country the whole app picks it up at once.
 */

/**
 * Country NAME → ISO alpha-2, built from the browser's own ISO tables.
 *
 * `/api/countries` returns `{id, name}` only — no code — so the name is all we
 * ever have to go on. `Intl.DisplayNames` maps code→name, so the map is built by
 * walking all 676 two-letter combinations once and keeping the ones the platform
 * recognises as a region (an unassigned code displays as itself). ~1ms, cached
 * for the session, and it stays correct without shipping a country table of our
 * own to maintain.
 */
let nameToCode = null;

const normalise = (s) => String(s).toLowerCase().replace(/[^a-z]/g, "");

function countryNameIndex() {
  if (nameToCode) return nameToCode;
  nameToCode = new Map();

  const display = typeof Intl?.DisplayNames === "function" ? new Intl.DisplayNames(["en"], { type: "region" }) : null;
  if (!display) return nameToCode;

  // First code wins: "United Kingdom" resolves from both GB and the non-ISO alias
  // UK, and react-country-flag only has a flag for GB.
  const add = (name, code) => {
    const key = normalise(name);
    if (key && !nameToCode.has(key)) nameToCode.set(key, code);
  };

  const A = "A".charCodeAt(0);
  for (let i = 0; i < 26; i += 1) {
    for (let j = 0; j < 26; j += 1) {
      const code = String.fromCharCode(A + i, A + j);
      let name;
      try {
        name = display.of(code);
      } catch {
        name = undefined;
      }
      // An unassigned code displays as the code itself — skip those.
      if (!name || name === code) continue;

      add(name, code);
      // The platform's long form is not what a store types: "Hong Kong SAR China"
      // vs "Hong Kong". Index the plain form too.
      const short = name.replace(/ SAR China$| \(.*\)$|^The /, "");
      if (short !== name) add(short, code);
    }
  }
  return nameToCode;
}

/**
 * @param {object} store  any object describing a store
 * @returns {string|null} upper-case ISO alpha-2, or null when the store states no country
 */
export function countryCodeOf(store) {
  const direct = store?.country_code || store?.country?.alpha2Code || store?.country?.code;
  if (direct) return String(direct).toUpperCase();

  const named =
    typeof store?.country === "string" ? store.country : store?.country?.name || store?.jurisdiction;
  if (named && named.length === 2) return named.toUpperCase();

  const name =
    named || (store?.country_id ? (Window.COUNTRIES || []).find((c) => c.id === store.country_id)?.name : null);

  return name ? countryNameIndex().get(normalise(name)) ?? null : null;
}

export default countryCodeOf;
